import type { ContractSelection } from './contractSelection';

export type OrderAction = 'BUY' | 'SELL';
export type TrailValueType = 'amount' | 'percent';

export interface ManualOrderValidationInput {
  editingOrder: boolean;
  unsupportedOrderType: string | null;
  symbol: string;
  quantity: string;
  orderType: string;
  price: string;
  tif: 'DAY' | 'GTC';
  outsideRth: boolean;
  trailingAmount: string;
}

export const getManualOrderValidationError = (
  input: ManualOrderValidationInput,
): string | null => {
  if (input.editingOrder && input.unsupportedOrderType) {
    return `IB order type ${input.unsupportedOrderType} cannot be edited safely from this form.`;
  }

  const quantity = parseInt(input.quantity);
  if (!input.symbol || isNaN(quantity) || quantity <= 0) {
    return 'Valid quantity (minimum 1) is required';
  }
  if (
    ['LIMIT', 'MIDPRICE', 'STOP'].includes(input.orderType)
    && (!input.price || parseFloat(input.price) <= 0)
  ) {
    return input.orderType === 'MIDPRICE'
      ? 'A positive price cap is required for MIDPRICE orders'
      : `Valid price is required for ${input.orderType} orders`;
  }
  if (input.orderType === 'MARKET' && input.tif === 'GTC') {
    return 'GTC is only available for limit and stop orders.';
  }
  if (input.orderType === 'MARKET' && input.outsideRth) {
    return 'Outside RTH is only available for limit and stop orders.';
  }
  if (input.orderType === 'TRAIL' && (!input.trailingAmount || parseFloat(input.trailingAmount) <= 0)) {
    return 'A valid trailing amount or percent is required for TRAIL orders.';
  }

  return null;
};

export interface OrderPriceFields {
  price?: number;
  trailing_amount?: number;
  trailing_percent?: number;
}

export const selectedContractMatchesInput = (
  contract: ContractSelection | null,
  inputSymbol: string,
): boolean => {
  if (!contract || !inputSymbol) return false;
  const normalizedInput = inputSymbol.toUpperCase();
  return [contract.symbol, contract.local_symbol, contract.display_symbol]
    .filter(Boolean)
    .some((value) => String(value).toUpperCase() === normalizedInput);
};

export const buildOrderPriceFields = (
  orderType: string,
  price: string,
  trailingAmount: string,
  trailType: TrailValueType,
): OrderPriceFields => ({
  price: ['LIMIT', 'MIDPRICE', 'STOP'].includes(orderType)
    ? parseFloat(parseFloat(price).toFixed(2))
    : undefined,
  trailing_amount: orderType === 'TRAIL' && trailType === 'amount'
    ? parseFloat(parseFloat(trailingAmount).toFixed(2))
    : undefined,
  trailing_percent: orderType === 'TRAIL' && trailType === 'percent'
    ? parseFloat(parseFloat(trailingAmount).toFixed(2))
    : undefined,
});

export interface BuildPlaceOrderInput {
  symbol: string;
  action: OrderAction;
  quantity: number;
  orderType: string;
  priceFields: OrderPriceFields;
  secType: string;
  strategyName: 'MANUAL' | 'NQMM';
  tif: 'DAY' | 'GTC';
  outsideRth: boolean;
}

export const buildPlaceOrderPayload = (
  input: BuildPlaceOrderInput,
  selectedContract: ContractSelection | null,
) => {
  const activeContract = selectedContractMatchesInput(selectedContract, input.symbol)
    ? selectedContract
    : null;
  const orderSymbol = activeContract?.symbol.toUpperCase() ?? input.symbol.toUpperCase();

  return {
    symbol: orderSymbol,
    action: input.action,
    quantity: input.quantity,
    order_type: input.orderType,
    ...input.priceFields,
    sec_type: input.secType,
    strategy_name: input.strategyName,
    tif: input.tif,
    outside_rth: input.outsideRth,
    ...(activeContract ? {
      con_id: activeContract.con_id ?? undefined,
      exchange: activeContract.exchange ?? undefined,
      currency: activeContract.currency ?? undefined,
      last_trade_date_or_contract_month: activeContract.last_trade_date_or_contract_month ?? undefined,
      local_symbol: activeContract.local_symbol ?? undefined,
      trading_class: activeContract.trading_class ?? undefined,
      multiplier: activeContract.multiplier ?? undefined,
    } : {}),
  };
};
